import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { API_BASE_URL, API_ENDPOINTS } from '../constants';
import { storageService } from '../services';
import type { HeatAlert } from '../types/heatAlert.types';

interface BehaviorDataPoint {
  ACTIVITY_INDEX: number;
  time_adjusted: string;
  baseline: number;
  cutoff1: number;
  cutoff2: number;
  cutoff3: number;
  cutoff4: number;
  cutoff5: number;
}

interface BehaviorChartProps {
  cowId: string;
  sensorId: string;
  startDate?: Date;
  endDate?: Date;
  heatAlerts?: HeatAlert[];
}

const BehaviorChart: React.FC<BehaviorChartProps> = ({
  cowId,
  sensorId,
  startDate,
  endDate,
  heatAlerts = [],
}) => {
  const [data, setData] = useState<BehaviorDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBehaviorData();
  }, [cowId, startDate, endDate]);

  const fetchBehaviorData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = storageService.getAuthToken();
      const params = new URLSearchParams();

      if (startDate) {
        params.append('startDate', format(startDate, 'yyyy-MM-dd HH:mm:ss'));
      }
      if (endDate) {
        params.append('endDate', format(endDate, 'yyyy-MM-dd HH:mm:ss'));
      }

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.BEHAVIORS.BY_COW(cowId)}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch behavior data');
      }

      const result = await response.json();
      setData(result.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatXAxis = (tickItem: string) => {
    try {
      return format(new Date(tickItem), 'MM/dd HH:mm');
    } catch {
      return tickItem;
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-300 p-3 rounded shadow-lg">
          <p className="font-semibold text-sm mb-2">
            {format(new Date(label), 'MMM dd, yyyy HH:mm')}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toFixed(2)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchBehaviorData}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-gray-500">ไม่พบข้อมูลพฤติกรรม</p>
      </div>
    );
  }

  // Filter active heat alerts within the date range
  const activeHeatAlerts = heatAlerts.filter(alert => {
    if (!alert.detectedAt || alert.status !== 'ACTIVE') return false;
    const alertDate = new Date(alert.detectedAt);
    if (startDate && alertDate < startDate) return false;
    if (endDate && alertDate > endDate) return false;
    return true;
  });

  // Debug: Log heat alerts
  console.log('🔍 Heat Alerts Debug:', {
    totalAlerts: heatAlerts.length,
    activeAlerts: activeHeatAlerts.length,
    alerts: activeHeatAlerts.map(a => ({
      id: a.id,
      detectedAt: a.detectedAt,
      formatted: format(new Date(a.detectedAt), "yyyy-MM-dd'T'HH:mm:ss")
    })),
    dataTimeRange: data.length > 0 ? {
      first: data[0].time_adjusted,
      last: data[data.length - 1].time_adjusted
    } : null
  });

  // Create a set of alert times for quick lookup
  const alertTimes = new Set(
    activeHeatAlerts.map(alert => {
      const alertDate = new Date(alert.detectedAt);
      return alertDate.toISOString();
    })
  );

  // Add heat alert markers to data
  const dataWithAlerts = data.map(point => {
    const pointDate = new Date(point.time_adjusted);

    // Check if there's an alert within 30 minutes of this data point
    const hasAlert = Array.from(alertTimes).some(alertTime => {
      const alertDate = new Date(alertTime);
      const timeDiff = Math.abs(pointDate.getTime() - alertDate.getTime());
      return timeDiff < 30 * 60 * 1000; // 30 minutes
    });

    return {
      ...point,
      heatAlert: hasAlert ? point.ACTIVITY_INDEX : null
    };
  });

  return (
    <div className="w-full">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            กราฟพฤติกรรมโค
          </h3>
          <p className="text-sm text-gray-600">Sensor ID: {sensorId}</p>
          {activeHeatAlerts.length > 0 && (
            <p className="text-sm text-red-600 mt-1">
              🔴 ตรวจพบการเป็นสัด {activeHeatAlerts.length} ครั้ง
            </p>
          )}
        </div>
        <div className="text-sm text-gray-500">
          ข้อมูล {data.length} รายการ
        </div>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={dataWithAlerts}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
          <XAxis
            dataKey="time_adjusted"
            tickFormatter={formatXAxis}
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
          />
          <YAxis
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            domain={['dataMin - 1', 'dataMax + 1']}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '12px' }}
            iconType="line"
          />

          {/* Activity Index - Main data line */}
          <Line
            type="monotone"
            dataKey="ACTIVITY_INDEX"
            stroke="#10b981"
            strokeWidth={2}
            dot={false}
            name="Activity Index"
            isAnimationActive={false}
          />

          {/* Heat Alert Markers - Red dots at alert positions */}
          <Line
            type="monotone"
            dataKey="heatAlert"
            stroke="none"
            strokeWidth={0}
            dot={{
              fill: '#ef4444',
              r: 8,
              strokeWidth: 2,
              stroke: '#fff'
            }}
            name="🔴 ตำแหน่งเป็นสัด"
            isAnimationActive={false}
            connectNulls={false}
          />

          {/* Baseline */}
          <Line
            type="monotone"
            dataKey="baseline"
            stroke="#9ca3af"
            strokeWidth={1.5}
            dot={false}
            name="Baseline"
            strokeDasharray="5 5"
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Legend Description */}
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-green-500"></div>
          <span>Activity Index - ค่ากิจกรรม</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-gray-400 border-dashed"></div>
          <span>Baseline - ค่าพื้นฐาน</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500 border-2 border-white"></div>
          <span>🔴 ตำแหน่งตรวจพบการเป็นสัด</span>
        </div>
      </div>
    </div>
  );
};

export default BehaviorChart;
